import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType, getAnalogTypesSet } from 'icreate/common/models/channel-type';
import { BarGraphChannel } from 'icreate/common/models/graphs';
import { lastValueFrom } from 'rxjs';
import { BarLabelDialogComponent } from '../bar-label-dialog/bar-label-dialog.component';
import { MoreOption, MoreOptionAction } from '@components/more-options/more-options-dropdown.component';

@Component({
    selector: 'app-bar-graph-table-row',
    templateUrl: './bar-graph-table-row.component.html',
    styleUrl: './bar-graph-table-row.component.css',
    standalone: false,
})
export class BarGraphTableRowComponent {
    @Input() rowIndex: number;
    @Input() channel: BarGraphChannel;
    @Input() vesselId: string;
    @Input() optionsVisible: boolean;
    @Output() onDeleteRow: EventEmitter<void> = new EventEmitter<void>();
    @Output() onOptionsToggled: EventEmitter<number> = new EventEmitter<number>();

    @ViewChild('otherOptions') otherOptions?: ElementRef<HTMLElement>;
    @ViewChild('iconButton') iconButton?: ElementRef<HTMLElement>;

    allowedTypesSet: Set<ChannelType> = getAnalogTypesSet();

    constructor(private dialog: MatDialog) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (
            !this.otherOptions?.nativeElement.contains(event.target) &&
            !this.iconButton?.nativeElement.contains(event.target) &&
            this.optionsVisible
        ) {
            this.onOptionsToggled.emit(this.rowIndex);
        }
    }

    ngAfterViewInit() {
        document.addEventListener(
            'scroll',
            () => {
                if (this.optionsVisible) {
                    this.onOptionsToggled.emit(this.rowIndex);
                }
            },
            true
        );
    }

    async openChannelSelector() {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vesselId,
                assignedChannel: this.channel.channel,
                allowedTypesSet: this.allowedTypesSet,
                setShouldDisableTypes: true,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
        if (channelSelected) {
            this.channel.channel = channelSelected;
        }
    }

    async openBarLabelSelector(): Promise<void> {
        const dialogRef = this.dialog.open(BarLabelDialogComponent, {
            disableClose: true,
            width: '474px',
            height: '284px',
            data: {
                barLabel: this.channel.barLabel,
                rowIndex: this.rowIndex + 1,
                channelTitle: this.getChannelTitle(),
                maxBarLabelLength: 16,
            },
        });
        const updatedBarLabel = await lastValueFrom(dialogRef.afterClosed());

        if (updatedBarLabel !== undefined) {
            this.channel.barLabel = updatedBarLabel;
        }
    }

    openOptions(): void {
        this.onOptionsToggled.emit(this.rowIndex);
    }

    clear(): void {
        if (this.clearIsDisabled()) {
            return;
        }
        this.channel.channel = null;
        this.channel.barLabel = null;
        this.onOptionsToggled.emit(this.rowIndex);
    }

    deleteRow() {
        this.onDeleteRow.emit();
        this.onOptionsToggled.emit(this.rowIndex);
    }

    clearIsDisabled() {
        return !this.channel?.channel && !this.channel?.barLabel;
    }

    getChannelTitle() {
        const fcuId = this.channel?.channel?.fcuData?.fcuChannelId;
        const name = this.channel?.channel?.opsData?.name;

        return fcuId ? `${fcuId} ${name ?? ''}` : null;
    }

    get moreOptions(): MoreOption[] {
        return [
            {
                action: MoreOptionAction.CLEAR,
                string: 'dlg.clear',
                disabled: this.clearIsDisabled(),
            },
            {
                action: MoreOptionAction.DELETE,
                string: 'dlg.delete',
            },
        ];
    }

    onMoreOptionSelected(action: MoreOptionAction): void {
        switch (action) {
            case MoreOptionAction.CLEAR:
                this.clear();
                break;

            case MoreOptionAction.DELETE:
                this.deleteRow();
                break;
        }
    }
}
